"use strict";

/**
 * @namespace
 */
gc.gis.geom = gc.gis.geom || {};

/**
 * Parameter für Geometrie-Operationen.
 *
 * @enum {int}
 */
gc.gis.geom.GCGeometryOperationParameters = {

	BUFFER_CAP_FLAT:    1,
	BUFFER_CAP_SQUARE:  2,
	BUFFER_CAP_ROUND:   3,

	BUFFER_DEFAULT_QUADRANT_SEGMENTS:   8
};

/**
 * Topology-Error-Objekt.
 *
 * @typedef gc.gis.geom.GCTopologyValidationError
 * @type {Object<string,*>}
 * @property {string} message Meldung
 * @property {number[]} coordinate Koordinate zum Fehler
 */

/**
 * @classdesc
 * Abstrakte Klasse für einen Geometrie-Prozessor.
 *
 * @class
 * @memberof gc.gis.geom
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 */
gc.gis.geom.GCGeometryProcessor = function()
{
};

/**
 * @method
 * @param {gc.gis.geom.GCGeometry} geometry Eingabegeometrie
 * @param {number} angle Drehwinkel in Radiant
 * @param {number[]} opt_anchor Rotationsanker standardmäßig der Mittelpunkt es Extent
 * @return {gc.gis.geom.GCGeometry}
 * @public
 */
gc.gis.geom.GCGeometryProcessor.prototype.rotate = gc.gis.abstractMethod;

/**
 * @method
 * @param {gc.gis.geom.GCGeometry} geometry Eingabegeometrie
 * @param {number} sx Skalierungsfaktor in X-Richtung
 * @param {number} sy Skalierungsfaktor in Y-Richtung
 * @return {gc.gis.geom.GCGeometry}
 * @public
 */
gc.gis.geom.GCGeometryProcessor.prototype.scale = gc.gis.abstractMethod;

/**
 * @method
 * @param {gc.gis.geom.GCGeometry} geometry Eingabegeometrie
 * @param {number} deltaX Delta in X-Richtung
 * @param {number} deltaY Delta in Y-Richtung
 * @return {gc.gis.geom.GCGeometry}
 * @public
 */
gc.gis.geom.GCGeometryProcessor.prototype.translate = gc.gis.abstractMethod;

/**
 * Ermittelt den geometrischen Puffer der Geometrie.
 *
 * @method
 * @param {gc.gis.geom.GCGeometry} geometry Eingabegeometrie
 * @param {double} distance
 * @param {int} quadrantSegments
 * @param {int} endCapStyle
 * @return {gc.gis.geom.GCGeometry}
 * @public
 */
gc.gis.geom.GCGeometryProcessor.prototype.buffer = gc.gis.abstractMethod;

/**
 * Ermittelt den geometrischen Schwerpunkt der Geometrie.
 *
 * @method
 * @param {gc.gis.geom.GCGeometry} geometry
 * @return {gc.gis.geom.GCGeometry}
 * @public
 */
gc.gis.geom.GCGeometryProcessor.prototype.centroid = gc.gis.abstractMethod;

/**
 * Ermittelt die konvexe Hülle der Geometrie.
 *
 * @param {gc.gis.geom.GCGeometry} geometry Eingabegeometrie
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.convexHull = gc.gis.abstractMethod;

/**
 * Ermittelt die konvexe Hülle über eine Menge von Geometrien.
 *
 * @param {gc.gis.geom.GCGeometry[]} geometry Eingabegeometrien
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.convexHullList = gc.gis.abstractMethod;

/**
 * Prüft, ob die Geometrie gültig ist.
 *
 * @param {gc.gis.geom.GCGeometry} geometry Eingabegeometrie
 * @return {bool}
 */
gc.gis.geom.GCGeometryProcessor.prototype.isValid = gc.gis.abstractMethod;

/**
 * Prüft, ob die Geometrie gültig ist und liefert im negativen Falle eine Objekt mit weiteren Hinweisen zum Fehler. Ist
 * die Geometrie valide, wird <code>null</code> zurückgegeben.
 *
 * @param {gc.gis.geom.GCGeometry} geometry Eingabegeometrie
 * @return {gc.gis.geom.GCTopologyValidationError}
 */
gc.gis.geom.GCGeometryProcessor.prototype.checkValid = gc.gis.abstractMethod;

/**
 * Ermittelt die Differenz der Geometrie von einer anderen Geometrie.
 *
 * @param {gc.gis.geom.GCGeometry} geometryA Geometrie A
 * @param {gc.gis.geom.GCGeometry} geometryB Geometrie B
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.difference = gc.gis.abstractMethod;

/**
 * Ermittelt die Differenz einer Geometrie mit einer Menge anderer Geometrien.
 *
 * @param {gc.gis.geom.GCGeometry} geometryA Geometrie A
 * @param {gc.gis.geom.GCGeometry[]} geometriesB Geometrie-Liste B
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.differenceList = gc.gis.abstractMethod;

/**
 * Ermittelt den Durchschnitt der Geometrie mit einer anderen Geometrie.
 *
 * @param {gc.gis.geom.GCGeometry} geometryA Geometrie A
 * @param {gc.gis.geom.GCGeometry} geometryB Geometrie B
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.intersection = gc.gis.abstractMethod;

/**
 * Ermittelt den Durchschnitt einer Geometrie mit einer Menge von Geometrien.
 *
 * @param {gc.gis.geom.GCGeometry} geometryA
 * @param {gc.gis.geom.GCGeometry[]} geometriesB Geometrie-Liste B
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.intersectionList = gc.gis.abstractMethod;

/**
 * Ermittelt die symmetrische Differenz der Geometrie von einer anderen Geometrie.
 *
 * @param {gc.gis.geom.GCGeometry} geometryA Geometrie A
 * @param {gc.gis.geom.GCGeometry} geometryB Geometrie B
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.symDifference = gc.gis.abstractMethod;

/**
 * Ermittelt die symmetrische Differenz der Geometrie mit einer Menge anderen Geometrien.
 *
 * @param {gc.gis.geom.GCGeometry} geometryA Geometrie A
 * @param {gc.gis.geom.GCGeometry[]} geometriesB Geometrie-Liste B
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.symDifferenceList = gc.gis.abstractMethod;

/**
 * Ermittelt die Vereinigung der Geometrie mit einer anderen Geometrie.
 *
 * @param {gc.gis.geom.GCGeometry} geometryA
 * @param {gc.gis.geom.GCGeometry} geometryB
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.union = gc.gis.abstractMethod;

/**
 * Ermittelt die Vereinigung der Geometrie mit einer anderen Geometrie.
 *
 * @param {gc.gis.geom.GCGeometry[]} geometries
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.geom.GCGeometryProcessor.prototype.cascadedUnion = gc.gis.abstractMethod;